/**
 * What a field *is*: its type, and the properties that type carries.
 *
 * Pure, and deliberately separate from the forms that edit it. A field's
 * configuration is a bag of properties named by the API, and the whole of the
 * type-change question -- which types may be offered, what a field of a new
 * type starts out as, what a save sends -- is answerable without rendering
 * anything.
 *
 * **An omitted property is left alone.** `PATCH` on a field applies only the
 * properties the body names, and the backend then narrows even those to the
 * ones the *target* type accepts. That is what lets a field whose type this
 * build cannot yet configure still be renamed, and what makes sending a field's
 * own type back a no-op rather than a conversion.
 */
import type { Field, FieldPropertiesOf, FieldTypeName, FieldUpdate } from '@/api/types'

import { constraintsToSend, indexToSend, isIndexed, isUnique } from './constraints'
import { DEFAULT_DURATION_FORMAT } from './duration'
import { DEFAULT_RATING_COLOR, DEFAULT_RATING_STYLE } from './rating'

/** The properties of a date field, which three field types share. */
const DATE_DEFAULTS = {
  date_format: 'EU',
  date_include_time: false,
  date_time_format: '24',
  date_show_tzinfo: false,
  date_force_timezone: null,
} as const satisfies FieldPropertiesOf<'date'>

/**
 * What a field of each type carries, and what a field that has just become one
 * starts with.
 *
 * An empty object is a real answer: the type has nothing to configure, because
 * an email field is an email field. It is distinct from a type being absent,
 * which `satisfies` makes impossible -- a twenty-eighth field type fails to
 * compile here until it is answered for.
 *
 * Every type has an answer now. Until the form view picker landed, the edit-row
 * link had `null` here, meaning "this build cannot configure this", and three
 * functions carried a branch for it. That branch is gone with its last
 * instance: a type with no form in `FIELD_FORMS` now means one thing only, that
 * there is nothing to configure.
 */
export const FIELD_DEFAULTS = {
  text: { text_default: '' },
  long_text: { long_text_enable_rich_text: false },
  url: {},
  email: {},
  number: {
    number_decimal_places: 0,
    number_negative: false,
    number_prefix: '',
    number_suffix: '',
    number_separator: '',
    // Null rather than the empty string, which the backend also accepts and
    // stores as null. One representation of "there is no default" is enough.
    number_default: null,
  },
  // The backend's own defaults for both, which is what a field created before
  // anybody chose is already carrying.
  rating: { max_value: 5, color: DEFAULT_RATING_COLOR, style: DEFAULT_RATING_STYLE },
  boolean: { boolean_default: false },
  date: DATE_DEFAULTS,
  last_modified: DATE_DEFAULTS,
  created_on: DATE_DEFAULTS,
  last_modified_by: {},
  created_by: {},
  duration: { duration_format: DEFAULT_DURATION_FORMAT },
  // The one property here the API insists on: `link_row_limit_selection_view_id`
  // is required rather than optional on the update body, so it is sent whether
  // or not anybody has limited anything.
  link_row: {
    link_row_table_id: null,
    has_related_field: true,
    link_row_multiple_relationships: true,
    link_row_limit_selection_view_id: null,
  },
  file: {},
  // The options *are* the field: everything a cell can hold is in this list, and
  // the order they are sent in is the order every dropdown over the field shows
  // them in.
  single_select: { select_options: [], single_select_default: null },
  multiple_select: { select_options: [], multiple_select_default: [] },
  phone_number: {},
  // The expression is the one property in this file the API insists on: every
  // other one may be omitted, and a formula field without a formula is not a
  // field.
  //
  // The formatting beside it is null until there is something to format. A
  // formula has no type of its own -- the backend works one out from the
  // expression -- so these are the properties of whichever type it turns out to
  // produce, and they have to be carried here so that saving a formula that
  // returns a date does not drop the format it was being read in.
  formula: {
    formula: '',
    number_decimal_places: null,
    number_prefix: '',
    number_suffix: '',
    number_separator: '',
    date_format: null,
    date_include_time: null,
    date_time_format: null,
    date_show_tzinfo: null,
    date_force_timezone: null,
    duration_format: null,
  },
  // The same formatting a formula carries, for the same reason: these three
  // have no type of their own either -- the backend works one out from what
  // they reach -- so the properties of whichever type that turns out to be have
  // to be carried, or saving one would drop the format it was being read in.
  count: { through_field_id: null },
  rollup: {
    through_field_id: null,
    target_field_id: null,
    // Not empty: an empty function name is refused, and refused in words about
    // a function nobody chose -- `argument number 1 given to function upper`.
    // Somebody changing a field into a rollup means to aggregate something, and
    // adding up is the aggregation they mean most often.
    rollup_function: 'sum',
    number_decimal_places: null,
    number_prefix: '',
    number_suffix: '',
    number_separator: '',
    date_format: null,
    date_include_time: null,
    date_time_format: null,
    date_show_tzinfo: null,
    date_force_timezone: null,
    duration_format: null,
  },
  lookup: {
    through_field_id: null,
    target_field_id: null,
    number_decimal_places: null,
    number_prefix: '',
    number_suffix: '',
    number_separator: '',
    date_format: null,
    date_include_time: null,
    date_time_format: null,
    date_show_tzinfo: null,
    date_force_timezone: null,
    duration_format: null,
  },
  multiple_collaborators: { notify_user_when_added: true },
  uuid: {},
  // `view_id` decides the order the rows are first numbered in, and it is only
  // read when a field *becomes* an autonumber. It is not a property the field
  // keeps, so it is not one this reads back -- `fieldUpdateBody` adds it to the
  // one request that can act on it.
  autonumber: {},
  password: { allow_endpoint_authentication: false },
  // The backend accepts a field pointing at no form -- its cells are simply
  // empty -- so null is a value here rather than an absence of one.
  form_view_edit_row: { form_view_id: null },
} as const satisfies { [T in FieldTypeName]: FieldPropertiesOf<T> }

/** A bag of properties belonging to whichever type is being configured. */
export type FieldProperties = Record<string, unknown>

/**
 * The properties a formula field carries because of what it returns.
 *
 * The same list as the formatting in `FIELD_DEFAULTS.formula`, and the two have
 * to agree: this is what the backend's own answer about an expression is read
 * for, and anything missing from it is formatting the field would keep showing
 * after the formula stopped producing that kind of value.
 */
export const FORMULA_FORMATTING_KEYS = [
  'number_decimal_places',
  'number_prefix',
  'number_suffix',
  'number_separator',
  'date_format',
  'date_include_time',
  'date_time_format',
  'date_show_tzinfo',
  'date_force_timezone',
  'duration_format',
] as const satisfies readonly (keyof FieldPropertiesOf<'formula'>)[]

/**
 * The field types a field may be changed into, in the order they are offered.
 *
 * Declared rather than derived from the map's key order, because the order
 * somebody reads is a different question from the order a record is written in:
 * the types that hold what you type come first, then the ones the database
 * fills in for you.
 */
export const FIELD_TYPE_ORDER = [
  'text',
  'long_text',
  'number',
  'rating',
  'boolean',
  'date',
  'duration',
  'url',
  'email',
  'phone_number',
  'file',
  'single_select',
  'multiple_select',
  'link_row',
  'multiple_collaborators',
  'password',
  'formula',
  'count',
  'rollup',
  'lookup',
  'created_on',
  'created_by',
  'last_modified',
  'last_modified_by',
  'autonumber',
  'uuid',
  'form_view_edit_row',
] as const satisfies readonly FieldTypeName[]

/**
 * The three types that cannot identify a row.
 *
 * A link row points at another table rather than saying anything about this one,
 * a password is stored so that it cannot be read back, and an edit-row link is a
 * link to the row it is on. The backend refuses each as a primary field; long
 * text is a fourth, but conditionally -- see `canBePrimary`.
 */
const NEVER_PRIMARY: ReadonlySet<FieldTypeName> = new Set([
  'link_row',
  'password',
  'form_view_edit_row',
])

/**
 * Whether a field of this type and configuration could be the primary field.
 *
 * Depends on the properties, not only on the type: rich text is stored as a
 * document rather than as a string, and the primary field has to render as the
 * one line that stands for the row.
 */
export function canBePrimary(type: FieldTypeName, properties: FieldProperties): boolean {
  if (NEVER_PRIMARY.has(type)) return false
  if (type === 'long_text') return properties['long_text_enable_rich_text'] !== true
  return true
}

/**
 * Whether an existing field could become the table's primary one.
 *
 * The same rule `offeredFieldTypes` applies to a field that already is primary,
 * asked of a field's own configuration rather than of a type and its defaults.
 * And exactly that rule: `change_primary_field` refuses on
 * `can_be_primary_field` and on nothing else, so a read-only field -- one a
 * data sync owns -- is a fair choice, and a synced table's identifying column
 * usually *is* the synced one.
 */
export function canBePrimaryField(field: Field): boolean {
  return canBePrimary(field.type, readFieldProperties(field, field.type))
}

/**
 * The types this dialog offers for a field.
 *
 * Narrowed once: a type that cannot identify a row is left out of a *primary*
 * field's list, because the backend refuses it. The field's own type is always
 * in the list, even when that rule would exclude it -- otherwise the picker
 * would show a field as being something it is not.
 */
export function offeredFieldTypes(field: Field): FieldTypeName[] {
  return FIELD_TYPE_ORDER.filter(
    (type) =>
      type === field.type ||
      field.primary !== true ||
      canBePrimary(type, FIELD_DEFAULTS[type]),
  )
}

/**
 * A field's configuration, read as the properties of the type it is becoming.
 *
 * Properties the two types share are carried across, which is what makes
 * changing a date into a last-modified keep its format rather than reset it.
 * Anything the target type has and the field does not gets the default, and so
 * does anything the field holds as null -- the backend nulls a property that
 * belonged to the old type, and null is not an answer any of these forms can
 * show.
 */
export function readFieldProperties(
  field: Field,
  type: FieldTypeName,
  /**
   * What the dialog is already holding, if anything.
   *
   * A property both types have keeps the value in the *draft*, not the one in
   * the field: somebody who picks a date format and then changes the field from
   * a date to a last-modified has not asked for their choice to be undone.
   * Everything else starts from the field, so a property that has no counterpart
   * in the new type cannot linger in the request as a key it does not accept.
   */
  drafted: FieldProperties = {},
): FieldProperties {
  const defaults = FIELD_DEFAULTS[type]
  const held = field as unknown as Record<string, unknown>
  const properties: FieldProperties = {}
  for (const [key, fallback] of Object.entries(defaults)) {
    const value = key in drafted ? drafted[key] : held[key]
    // Null and absent are treated alike. A property that belonged to the type
    // before this one comes back null, and null is not something a form can
    // show -- except where the default is *itself* null, which is how "there is
    // no default" and "follow whoever is looking" are written, and where
    // falling back to it changes nothing.
    properties[key] = value == null ? fallback : value
  }
  return properties
}

/** Everything the dialog is holding about a field. */
export interface FieldDraft {
  name: string
  type: FieldTypeName
  description: string
  aiDescription: string
  properties: FieldProperties
  /**
   * Whether the column refuses duplicates, and whether the database indexes it.
   *
   * Beside the properties rather than in them: neither belongs to a type. They
   * are kept as chosen even while the type cannot honour them -- see
   * `constraintsToSend`, which narrows at the last moment.
   */
  unique: boolean
  indexed: boolean
}

/** The draft a dialog opens on. */
export function draftFromField(field: Field): FieldDraft {
  return {
    name: field.name,
    type: field.type,
    description: field.description ?? '',
    aiDescription: field.ai_description ?? '',
    properties: readFieldProperties(field, field.type),
    unique: isUnique(field),
    indexed: isIndexed(field),
  }
}

/**
 * The body of the request that saves a draft.
 *
 * The type is always sent, including when it has not changed. Sending it is
 * what the API's own discriminator asks for, and it costs nothing: the handler
 * compares it against the field's current type and only converts when they
 * differ.
 *
 * The description and the AI description are sent as null when they are blank,
 * rather than as the empty string. They are two ways of saying the same thing to
 * the API, and one of them is the one it sends back.
 *
 * The index and the constraints are always sent, and always narrowed to what
 * the type being saved will accept. Leaving either out is what makes a unique
 * or indexed field impossible to change the type of: the backend validates
 * whatever the field is still carrying against the type it is becoming, and
 * refuses.
 */
export function fieldUpdateBody(
  field: Field,
  draft: FieldDraft,
  { viewId }: { viewId: number | undefined } = { viewId: undefined },
): FieldUpdate {
  const becomingAutonumber = draft.type === 'autonumber' && field.type !== 'autonumber'

  return {
    ...draft.properties,
    ...(becomingAutonumber && viewId !== undefined ? { view_id: viewId } : {}),
    type: draft.type,
    name: draft.name,
    db_index: indexToSend(draft.type, draft.indexed),
    field_constraints: constraintsToSend(draft.type, draft.unique),
    description: draft.description === '' ? null : draft.description,
    ai_description: draft.aiDescription === '' ? null : draft.aiDescription,
    // The bag is keyed by strings, so the union cannot be checked against it.
    // Every key in it came from `FIELD_DEFAULTS`, which is checked.
  } as FieldUpdate
}
